import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  writeBatch,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './config';
import { autoCreateNotifications } from './notificationService';
import { autoCreateLogs } from './logService';

// Backup service for system health management
export const backupService = {
  // Create a complete system backup
  createBackup: async (backupName = null) => {
    try {
      console.log('🔄 Starting system backup...');
      
      // Get all collections data
      const collections = ['users', 'apartments', 'payments', 'maintenance', 'notices', 'messages', 'notifications'];
      const backupData = {};
      
      // Fetch data from all collections
      for (const collectionName of collections) {
        try {
          const querySnapshot = await getDocs(collection(db, collectionName));
          const documents = [];
          querySnapshot.forEach((doc) => {
            documents.push({
              id: doc.id,
              ...doc.data()
            });
          });
          backupData[collectionName] = documents;
          console.log(`📦 Backed up ${documents.length} documents from ${collectionName}`);
        } catch (error) {
          console.error(`❌ Error backing up ${collectionName}:`, error);
          backupData[collectionName] = [];
        }
      }
      
      // Create backup metadata
      const backupMetadata = {
        name: backupName || `Backup_${new Date().toISOString().split('T')[0]}`,
        timestamp: serverTimestamp(),
        version: '1.0',
        collections: Object.keys(backupData),
        totalDocuments: Object.values(backupData).reduce((sum, docs) => sum + docs.length, 0),
        createdBy: 'system',
        description: backupName ? `Manual backup: ${backupName}` : 'Automatic system backup',
        type: backupName ? 'manual' : 'automatic'
      };
      
      // Store backup in Firestore
      const backupRef = await addDoc(collection(db, 'backups'), {
        metadata: backupMetadata,
        data: backupData
      });
      
      console.log(`✅ Backup created successfully: ${backupMetadata.name} (ID: ${backupRef.id})`);
      console.log(`📊 Total documents backed up: ${backupMetadata.totalDocuments}`);
      
      // Create notification for backup creation
      const notificationData = {
        id: backupRef.id,
        name: backupMetadata.name,
        documentCount: backupMetadata.totalDocuments
      };
      
      try {
        await autoCreateNotifications.onBackupOperation(notificationData, 'created');
        console.log('📢 Backup notification sent');
      } catch (notificationError) {
        console.warn('⚠️ Failed to send backup notification:', notificationError);
      }
      
      // Create log entry for backup creation
      try {
        await autoCreateLogs.onSystemOperation(notificationData, 'backup_created', 'system');
        console.log('📝 Backup log entry created');
      } catch (logError) {
        console.warn('⚠️ Failed to create backup log entry:', logError);
      }
      
      return {
        success: true,
        backupId: backupRef.id,
        metadata: backupMetadata,
        documentCount: backupMetadata.totalDocuments
      };
      
    } catch (error) {
      console.error('❌ Backup creation failed:', error);
      
      // Create notification for backup failure
      const notificationData = {
        id: null,
        name: backupName || 'Unknown Backup',
        documentCount: 0,
        error: error.message
      };
      
      try {
        await autoCreateNotifications.onBackupOperation(notificationData, 'failed');
        console.log('📢 Backup failure notification sent');
      } catch (notificationError) {
        console.warn('⚠️ Failed to send backup failure notification:', notificationError);
      }
      
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Get all available backups
  getAllBackups: async () => {
    try {
      const q = query(
        collection(db, 'backups'),
        orderBy('metadata.timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const backups = [];
      querySnapshot.forEach((doc) => {
        const backup = {
          id: doc.id,
          ...doc.data()
        };
        backups.push(backup);
      });
      
      return {
        success: true,
        data: backups
      };
    } catch (error) {
      console.error('❌ Error fetching backups:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Get backup by ID
  getBackupById: async (backupId) => {
    try {
      const backupDoc = await getDoc(doc(db, 'backups', backupId));
      
      if (!backupDoc.exists()) {
        return {
          success: false,
          error: {
            code: 'backup-not-found',
            message: 'Backup not found'
          }
        };
      }
      
      return {
        success: true,
        data: {
          id: backupDoc.id,
          ...backupDoc.data()
        }
      };
    } catch (error) {
      console.error('❌ Error fetching backup:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Restore system from backup
  restoreFromBackup: async (backupId, options = {}) => {
    try {
      console.log('🔄 Starting system restore...');
      
      // Get backup data
      const backupResult = await backupService.getBackupById(backupId);
      if (!backupResult.success) {
        return backupResult;
      }
      
      const backup = backupResult.data;
      const { data: backupData, metadata } = backup;
      
      console.log(`📦 Restoring from backup: ${metadata.name}`);
      console.log(`📊 Total documents to restore: ${metadata.totalDocuments}`);
      
      // Confirmation options
      const {
        clearExisting = false, // Whether to clear existing data before restore
        collections = Object.keys(backupData) // Which collections to restore
      } = options;
      
      const restoreResults = {};
      const batch = writeBatch(db);
      
      // Process each collection
      for (const collectionName of collections) {
        if (!backupData[collectionName]) {
          console.log(`⚠️ Collection ${collectionName} not found in backup, skipping...`);
          continue;
        }
        
        const documents = backupData[collectionName];
        console.log(`🔄 Restoring ${documents.length} documents to ${collectionName}...`);
        
        if (clearExisting) {
          // Clear existing documents in this collection
          try {
            const existingDocs = await getDocs(collection(db, collectionName));
            existingDocs.forEach((doc) => {
              batch.delete(doc.ref);
            });
            console.log(`🗑️ Cleared ${existingDocs.size} existing documents from ${collectionName}`);
          } catch (error) {
            console.error(`❌ Error clearing ${collectionName}:`, error);
          }
        }
        
        // Add restore operations to batch
        let restoredCount = 0;
        for (const document of documents) {
          try {
            const { id, ...documentData } = document;
            const docRef = doc(db, collectionName, id);
            
            batch.set(docRef, {
              ...documentData,
              restoredAt: serverTimestamp(),
              restoredFrom: backupId
            });
            restoredCount++;
          } catch (error) {
            console.error(`❌ Error preparing restore for ${collectionName}/${document.id}:`, error);
          }
        }
        
        restoreResults[collectionName] = {
          total: documents.length,
          restored: restoredCount,
          errors: documents.length - restoredCount
        };
        
        console.log(`✅ ${collectionName}: ${restoredCount}/${documents.length} documents prepared for restore`);
      }
      
      // Execute batch
      console.log('🚀 Executing restore batch...');
      await batch.commit();
      console.log('✅ Restore batch executed successfully');
      
      // Create restore record
      const restoreRecord = {
        backupId,
        backupName: metadata.name,
        restoredAt: serverTimestamp(),
        restoredBy: 'admin', // TODO: Get actual user
        options,
        results: restoreResults,
        totalRestored: Object.values(restoreResults).reduce((sum, result) => sum + result.restored, 0)
      };
      
      await addDoc(collection(db, 'restores'), restoreRecord);
      console.log('📝 Restore record created');
      
      // Create notification for restore completion
      const notificationData = {
        backupId,
        backupName: metadata.name,
        totalRestored: Object.values(restoreResults).reduce((sum, result) => sum + result.restored, 0)
      };
      
      try {
        await autoCreateNotifications.onRestoreOperation(notificationData, 'completed');
        console.log('📢 Restore completion notification sent');
      } catch (notificationError) {
        console.warn('⚠️ Failed to send restore notification:', notificationError);
      }
      
      // Create log entry for restore completion
      try {
        const logData = {
          ...notificationData,
          totalRestored: Object.values(restoreResults).reduce((sum, result) => sum + result.restored, 0)
        };
        await autoCreateLogs.onSystemOperation(logData, 'restore_performed', 'system');
        console.log('📝 Restore completion log entry created');
      } catch (logError) {
        console.warn('⚠️ Failed to create restore completion log entry:', logError);
      }
      
      return {
        success: true,
        results: restoreResults,
        totalRestored: Object.values(restoreResults).reduce((sum, result) => sum + result.restored, 0),
        backupName: metadata.name
      };
      
    } catch (error) {
      console.error('❌ Restore failed:', error);
      
      // Create notification for restore failure
      const notificationData = {
        backupId: backupId || 'unknown',
        backupName: 'Unknown Backup',
        totalRestored: 0,
        error: error.message
      };
      
      try {
        await autoCreateNotifications.onRestoreOperation(notificationData, 'failed');
        console.log('📢 Restore failure notification sent');
      } catch (notificationError) {
        console.warn('⚠️ Failed to send restore failure notification:', notificationError);
      }
      
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Delete backup
  deleteBackup: async (backupId) => {
    try {
      // Get backup data before deletion for notification
      const backupDoc = await getDoc(doc(db, 'backups', backupId));
      let backupName = 'Unknown Backup';
      
      if (backupDoc.exists()) {
        backupName = backupDoc.data().metadata.name;
      }
      
      await deleteDoc(doc(db, 'backups', backupId));
      console.log(`🗑️ Backup ${backupId} deleted successfully`);
      
      // Create notification for backup deletion
      const notificationData = {
        id: backupId,
        name: backupName,
        documentCount: 0
      };
      
      try {
        await autoCreateNotifications.onBackupOperation(notificationData, 'deleted');
        console.log('📢 Backup deletion notification sent');
      } catch (notificationError) {
        console.warn('⚠️ Failed to send backup deletion notification:', notificationError);
      }
      
      // Create log entry for backup deletion
      try {
        await autoCreateLogs.onSystemOperation(notificationData, 'backup_deleted', 'system');
        console.log('📝 Backup deletion log entry created');
      } catch (logError) {
        console.warn('⚠️ Failed to create backup deletion log entry:', logError);
      }
      
      return {
        success: true
      };
    } catch (error) {
      console.error('❌ Error deleting backup:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Get restore history
  getRestoreHistory: async () => {
    try {
      const q = query(
        collection(db, 'restores'),
        orderBy('restoredAt', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      
      const restores = [];
      querySnapshot.forEach((doc) => {
        restores.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return {
        success: true,
        data: restores
      };
    } catch (error) {
      console.error('❌ Error fetching restore history:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Schedule automatic backup (runs daily)
  scheduleAutomaticBackup: async () => {
    try {
      // Check if automatic backup is already scheduled for today
      const today = new Date().toISOString().split('T')[0];
      const q = query(
        collection(db, 'backups'),
        where('metadata.name', '==', `Backup_${today}`),
        where('metadata.type', '==', 'automatic')
      );
      
      const existingBackup = await getDocs(q);
      
      if (!existingBackup.empty) {
        console.log('✅ Automatic backup already exists for today');
        return {
          success: true,
          message: 'Automatic backup already exists for today'
        };
      }
      
      // Create automatic backup
      const backupResult = await backupService.createBackup();
      
      // Create notification for automatic backup scheduling
      if (backupResult.success) {
        const notificationData = {
          id: backupResult.backupId,
          name: backupResult.metadata.name,
          documentCount: backupResult.documentCount
        };
        
        try {
          await autoCreateNotifications.onBackupOperation(notificationData, 'scheduled');
          console.log('📢 Automatic backup notification sent');
        } catch (notificationError) {
          console.warn('⚠️ Failed to send automatic backup notification:', notificationError);
        }
      }
      
      return backupResult;
      
    } catch (error) {
      console.error('❌ Automatic backup scheduling failed:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Get system health statistics
  getSystemHealth: async () => {
    try {
      const collections = ['users', 'apartments', 'payments', 'maintenance', 'notices', 'messages', 'notifications'];
      const healthData = {};
      const healthIssues = [];
      
      // Check each collection
      for (const collectionName of collections) {
        try {
          const querySnapshot = await getDocs(collection(db, collectionName));
          healthData[collectionName] = {
            documentCount: querySnapshot.size,
            lastUpdated: new Date().toISOString(),
            status: 'healthy'
          };
          
          // Collection-specific health checks
          if (collectionName === 'users') {
            const adminUsers = querySnapshot.docs.filter(doc => doc.data().role === 'admin');
            if (adminUsers.length === 0) {
              healthData[collectionName].status = 'warning';
              healthData[collectionName].issue = 'No admin users found';
              healthIssues.push(`No admin users in ${collectionName}`);
            }
          }
          
          if (collectionName === 'apartments') {
            const occupiedApartments = querySnapshot.docs.filter(doc => doc.data().status === 'Occupied');
            const totalApartments = querySnapshot.size;
            const occupancyRate = totalApartments > 0 ? (occupiedApartments.length / totalApartments) * 100 : 0;
            
            if (occupancyRate > 95) {
              healthData[collectionName].status = 'warning';
              healthData[collectionName].issue = 'High occupancy rate';
              healthIssues.push(`High occupancy rate in ${collectionName}: ${occupancyRate.toFixed(1)}%`);
            }
          }
          
          if (collectionName === 'payments') {
            const overduePayments = querySnapshot.docs.filter(doc => doc.data().status === 'Overdue');
            const totalPayments = querySnapshot.size;
            const overdueRate = totalPayments > 0 ? (overduePayments.length / totalPayments) * 100 : 0;
            
            if (overdueRate > 20) {
              healthData[collectionName].status = 'warning';
              healthData[collectionName].issue = 'High overdue payment rate';
              healthIssues.push(`High overdue rate in ${collectionName}: ${overdueRate.toFixed(1)}%`);
            }
          }
          
          if (collectionName === 'maintenance') {
            const pendingMaintenance = querySnapshot.docs.filter(doc => doc.data().status === 'Pending');
            const totalMaintenance = querySnapshot.size;
            const pendingRate = totalMaintenance > 0 ? (pendingMaintenance.length / totalMaintenance) * 100 : 0;
            
            if (pendingRate > 30) {
              healthData[collectionName].status = 'warning';
              healthData[collectionName].issue = 'High pending maintenance rate';
              healthIssues.push(`High pending rate in ${collectionName}: ${pendingRate.toFixed(1)}%`);
            }
          }
          
        } catch (error) {
          healthData[collectionName] = {
            documentCount: 0,
            error: error.message,
            lastUpdated: new Date().toISOString(),
            status: 'error'
          };
          healthIssues.push(`Error accessing ${collectionName}: ${error.message}`);
        }
      }
      
      // Get latest backup info
      const latestBackupQuery = query(
        collection(db, 'backups'),
        orderBy('metadata.timestamp', 'desc'),
        limit(1)
      );
      const latestBackupSnapshot = await getDocs(latestBackupQuery);
      const latestBackup = latestBackupSnapshot.empty ? null : {
        id: latestBackupSnapshot.docs[0].id,
        ...latestBackupSnapshot.docs[0].data()
      };
      
      // Check backup health
      let backupStatus = 'healthy';
      let backupIssue = null;
      
      if (!latestBackup) {
        backupStatus = 'warning';
        backupIssue = 'No backups found';
        healthIssues.push('No system backups available');
      } else {
        const backupAge = Date.now() - (latestBackup.metadata.timestamp.seconds * 1000);
        const backupAgeDays = backupAge / (1000 * 60 * 60 * 24);
        
        if (backupAgeDays > 7) {
          backupStatus = 'warning';
          backupIssue = `Backup is ${backupAgeDays.toFixed(1)} days old`;
          healthIssues.push(`Backup is ${backupAgeDays.toFixed(1)} days old`);
        }
      }
      
      // Determine overall system status
      let systemStatus = 'Healthy';
      let systemStatusColor = 'success';
      
      const errorCollections = Object.values(healthData).filter(data => data.status === 'error').length;
      const warningCollections = Object.values(healthData).filter(data => data.status === 'warning').length;
      
      if (errorCollections > 0) {
        systemStatus = 'Critical';
        systemStatusColor = 'error';
      } else if (warningCollections > 2 || healthIssues.length > 3) {
        systemStatus = 'Warning';
        systemStatusColor = 'warning';
      } else if (warningCollections > 0 || healthIssues.length > 0) {
        systemStatus = 'Attention';
        systemStatusColor = 'info';
      }
      
      // Calculate system metrics
      const totalDocuments = Object.values(healthData).reduce((sum, data) => sum + (data.documentCount || 0), 0);
      const healthyCollections = Object.values(healthData).filter(data => data.status === 'healthy').length;
      const totalCollections = collections.length;
      const healthScore = totalCollections > 0 ? (healthyCollections / totalCollections) * 100 : 0;
      
      return {
        success: true,
        data: {
          collections: healthData,
          totalDocuments,
          latestBackup: latestBackup ? {
            ...latestBackup,
            status: backupStatus,
            issue: backupIssue
          } : null,
          systemStatus,
          systemStatusColor,
          healthScore: Math.round(healthScore),
          healthIssues,
          metrics: {
            totalCollections,
            healthyCollections,
            warningCollections,
            errorCollections,
            totalIssues: healthIssues.length
          }
        }
      };
    } catch (error) {
      console.error('❌ Error getting system health:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Clear data from specified collections
  clearData: async (collections = [], options = {}) => {
    try {
      console.log('🗑️ Starting data clearance...');
      
      const {
        preserveAdmins = true, // Keep admin users
        preserveSystemData = true // Keep system-critical data
      } = options;
      
      const clearResults = {};
      const batch = writeBatch(db);
      
      // Process each collection
      for (const collectionName of collections) {
        try {
          console.log(`🔄 Processing ${collectionName} collection...`);
          
          const querySnapshot = await getDocs(collection(db, collectionName));
          let documentsToDelete = [];
          
          // Filter documents based on options
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            let shouldDelete = true;
            
            // Preserve admin users if requested
            if (collectionName === 'users' && preserveAdmins) {
              if (data.role === 'admin') {
                shouldDelete = false;
                console.log(`👤 Preserving admin user: ${data.email || data.uid}`);
              }
            }
            
            // Preserve system-critical data if requested
            if (preserveSystemData) {
              if (collectionName === 'notifications' && data.type === 'system') {
                shouldDelete = false;
                console.log(`🔧 Preserving system notification: ${doc.id}`);
              }
            }
            
            if (shouldDelete) {
              documentsToDelete.push(doc);
            }
          });
          
          console.log(`📊 ${collectionName}: ${documentsToDelete.length}/${querySnapshot.size} documents will be cleared`);
          
          // Add delete operations to batch
          documentsToDelete.forEach((doc) => {
            batch.delete(doc.ref);
          });
          
          clearResults[collectionName] = {
            total: querySnapshot.size,
            cleared: documentsToDelete.length,
            preserved: querySnapshot.size - documentsToDelete.length
          };
          
        } catch (error) {
          console.error(`❌ Error processing ${collectionName}:`, error);
          clearResults[collectionName] = {
            total: 0,
            cleared: 0,
            error: error.message
          };
        }
      }
      
      // Execute batch
      console.log('🚀 Executing clear batch...');
      await batch.commit();
      console.log('✅ Clear batch executed successfully');
      
      // Create clear record
      const clearRecord = {
        clearedAt: serverTimestamp(),
        clearedBy: 'admin', // TODO: Get actual user
        collections,
        options,
        results: clearResults,
        totalCleared: Object.values(clearResults).reduce((sum, result) => sum + (result.cleared || 0), 0)
      };
      
      await addDoc(collection(db, 'dataClears'), clearRecord);
      console.log('📝 Clear record created');
      
      // Create log entry for data clearance
      try {
        const logData = {
          totalCleared: Object.values(clearResults).reduce((sum, result) => sum + (result.cleared || 0), 0),
          collections: collections,
          options: options
        };
        await autoCreateLogs.onSystemOperation(logData, 'data_cleared', 'system');
        console.log('📝 Data clearance log entry created');
      } catch (logError) {
        console.warn('⚠️ Failed to create data clearance log entry:', logError);
      }
      
      return {
        success: true,
        results: clearResults,
        totalCleared: Object.values(clearResults).reduce((sum, result) => sum + (result.cleared || 0), 0)
      };
      
    } catch (error) {
      console.error('❌ Data clearance failed:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  },

  // Get clear history
  getClearHistory: async () => {
    try {
      const q = query(
        collection(db, 'dataClears'),
        orderBy('clearedAt', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      
      const clears = [];
      querySnapshot.forEach((doc) => {
        clears.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return {
        success: true,
        data: clears
      };
    } catch (error) {
      console.error('❌ Error fetching clear history:', error);
      return {
        success: false,
        error: {
          code: error.code,
          message: error.message
        }
      };
    }
  }
}; 